import CustomButton from 'Components/CustomButton'
import DrawerRight from 'Components/DrawerRight'
import StyledForm from 'Components/StyledForm'
import { ActivePeriodTimePicker } from 'Services/ProductManagement/Features/Product/Components/ActivePeriodTimePicker'
import {
  DaysOfTheWeek,
  PricingTabType,
  type Validity
} from 'Services/ProductManagement/Features/Product/productSlice'
import { useUnsavedChanges } from 'Util/Context/unsavedChangesContext'
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import { Box } from '@mui/system'
import { useEffect, useState } from 'react'
import { t } from 'ttag'

interface ProductPricingActivePeriodDrawerProps {
  open: boolean
  selectedActivePeriod: Validity | null
  handleClose: () => void
  activePeriods: (Validity | null)[]
  onSubmit: (activePeriod: Validity) => void
  selectedTab: PricingTabType
}

export default function ProductPricingActivePeriodDrawer({
  open,
  selectedActivePeriod,
  handleClose,
  activePeriods,
  onSubmit,
  selectedTab
}: Readonly<ProductPricingActivePeriodDrawerProps>) {
  const initialActivePeriod = selectedActivePeriod ?? {
    days_of_week: [] as DaysOfTheWeek[],
    exceptions: [],
    time_of_day_from: null,
    time_of_day_until: null,
    title: '',
    valid_from: '',
    valid_now: true,
    valid_until: '',
    zone_id: null
  }
  const [activePeriod, setActivePeriod] = useState<Validity>(initialActivePeriod)
  const [titleFieldError, setTitleFieldError] = useState<string | null>(null)

  const [everyDayChecked, setEveryDayChecked] = useState(false)
  const [weekDaysChecked, setWeekDaysChecked] = useState(false)
  const [saturdayChecked, setSaturdayChecked] = useState(false)
  const [sundayChecked, setSundayChecked] = useState(false)

  const [wholeDayChecked, setWholeDayChecked] = useState(true)

  const { markEdited, clearEdited, hasUnsavedChanges, confirmForUnsavedChanges } =
    useUnsavedChanges()

  useEffect(() => {
    if (weekDaysChecked && saturdayChecked && sundayChecked) {
      setEveryDayChecked(true)
      setActivePeriod(prevState => ({
        ...prevState,
        days_of_week: [
          DaysOfTheWeek.MONDAY,
          DaysOfTheWeek.TUESDAY,
          DaysOfTheWeek.WEDNESDAY,
          DaysOfTheWeek.THURSDAY,
          DaysOfTheWeek.FRIDAY,
          DaysOfTheWeek.SATURDAY,
          DaysOfTheWeek.SUNDAY
        ]
      }))
      setWeekDaysChecked(false)
      setSaturdayChecked(false)
      setSundayChecked(false)
    }

    if (everyDayChecked) {
      setWeekDaysChecked(false)
      setSaturdayChecked(false)
      setSundayChecked(false)
      setActivePeriod(prevState => ({
        ...prevState,
        days_of_week: [
          DaysOfTheWeek.MONDAY,
          DaysOfTheWeek.TUESDAY,
          DaysOfTheWeek.WEDNESDAY,
          DaysOfTheWeek.THURSDAY,
          DaysOfTheWeek.FRIDAY,
          DaysOfTheWeek.SATURDAY,
          DaysOfTheWeek.SUNDAY
        ]
      }))
    }
  }, [everyDayChecked, weekDaysChecked, saturdayChecked, sundayChecked])

  useEffect(() => {
    if (selectedActivePeriod) {
      setActivePeriod(selectedActivePeriod)
      setEveryDayChecked(
        selectedActivePeriod.days_of_week.length === 7 &&
          selectedActivePeriod.days_of_week.every(day =>
            [
              DaysOfTheWeek.MONDAY,
              DaysOfTheWeek.TUESDAY,
              DaysOfTheWeek.WEDNESDAY,
              DaysOfTheWeek.THURSDAY,
              DaysOfTheWeek.FRIDAY,
              DaysOfTheWeek.SATURDAY,
              DaysOfTheWeek.SUNDAY
            ].includes(day)
          )
      )
      setWeekDaysChecked(
        selectedActivePeriod.days_of_week.length < 7 &&
          selectedActivePeriod.days_of_week.some(day =>
            [
              DaysOfTheWeek.MONDAY,
              DaysOfTheWeek.TUESDAY,
              DaysOfTheWeek.WEDNESDAY,
              DaysOfTheWeek.THURSDAY,
              DaysOfTheWeek.FRIDAY
            ].includes(day)
          )
      )
      setSaturdayChecked(selectedActivePeriod.days_of_week.includes(DaysOfTheWeek.SATURDAY))
      setSundayChecked(selectedActivePeriod.days_of_week.includes(DaysOfTheWeek.SUNDAY))
      setWholeDayChecked(
        selectedActivePeriod.time_of_day_from === null &&
          selectedActivePeriod.time_of_day_until === null
      )
    } else {
      setActivePeriod(initialActivePeriod)
    }
  }, [selectedActivePeriod])

  const handleEveryDayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target

    setActivePeriod(prevState => ({
      ...prevState,
      days_of_week: checked
        ? [
            DaysOfTheWeek.MONDAY,
            DaysOfTheWeek.TUESDAY,
            DaysOfTheWeek.WEDNESDAY,
            DaysOfTheWeek.THURSDAY,
            DaysOfTheWeek.FRIDAY,
            DaysOfTheWeek.SATURDAY,
            DaysOfTheWeek.SUNDAY
          ]
        : []
    }))
    setEveryDayChecked(checked)
    markEdited()
  }

  const handleWeekDaysChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target
    setActivePeriod(prevState => ({
      ...prevState,
      days_of_week: checked
        ? [
            ...prevState.days_of_week,
            DaysOfTheWeek.MONDAY,
            DaysOfTheWeek.TUESDAY,
            DaysOfTheWeek.WEDNESDAY,
            DaysOfTheWeek.THURSDAY,
            DaysOfTheWeek.FRIDAY
          ]
        : prevState.days_of_week.filter(
            day =>
              ![
                DaysOfTheWeek.MONDAY,
                DaysOfTheWeek.TUESDAY,
                DaysOfTheWeek.WEDNESDAY,
                DaysOfTheWeek.THURSDAY,
                DaysOfTheWeek.FRIDAY
              ].includes(day)
          )
    }))
    setWeekDaysChecked(checked)
    markEdited()
  }

  const handleSaturdayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target
    setActivePeriod(prevState => ({
      ...prevState,
      days_of_week: checked
        ? [...prevState.days_of_week, DaysOfTheWeek.SATURDAY]
        : prevState.days_of_week.filter(day => day !== DaysOfTheWeek.SATURDAY)
    }))
    setSaturdayChecked(checked)
    markEdited()
  }

  const handleSundayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target
    setActivePeriod(prevState => ({
      ...prevState,
      days_of_week: checked
        ? [...prevState.days_of_week, DaysOfTheWeek.SUNDAY]
        : prevState.days_of_week.filter(day => day !== DaysOfTheWeek.SUNDAY)
    }))
    setSundayChecked(checked)
    markEdited()
  }

  const handleWholeDayCheckedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target
    setWholeDayChecked(checked)
    if (checked) {
      setActivePeriod(prevState => ({
        ...prevState,
        time_of_day_from: null,
        time_of_day_until: null
      }))
    } else {
      setActivePeriod(prevState => ({
        ...prevState,
        time_of_day_from: initialActivePeriod.time_of_day_from,
        time_of_day_until: initialActivePeriod.time_of_day_until
      }))
    }
    markEdited()
  }

  const handleChange = (
    value: string | string[] | number | boolean | undefined,
    field: keyof Validity
  ) => {
    if (value !== undefined) {
      setActivePeriod(prevState => ({ ...prevState, [field]: value }) as Validity)
      markEdited()
    }
  }

  const handleTimePickerChange = (value: Date | null, name: string) => {
    if (value instanceof Date) {
      // Extract the time portion in HH:MM:SS format
      const hours = value.getHours().toString().padStart(2, '0')
      const minutes = value.getMinutes().toString().padStart(2, '0')
      const seconds = value.getSeconds().toString().padStart(2, '0')
      const timeValue = `${hours}:${minutes}:${seconds}`
      handleChange(timeValue, name as keyof Validity)
    }
  }

  const resetDrawerBackToInitialValues = () => {
    setActivePeriod(initialActivePeriod)
    setEveryDayChecked(false)
    setWeekDaysChecked(false)
    setSaturdayChecked(false)
    setSundayChecked(false)
    setWholeDayChecked(true)
    setTitleFieldError(null)
    clearEdited()
  }

  const handleDrawerClose = () => {
    confirmForUnsavedChanges(() => {
      resetDrawerBackToInitialValues()
      handleClose()
    })
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (
      selectedActivePeriod?.title !== activePeriod.title &&
      activePeriods.some(period => period?.title === activePeriod.title)
    ) {
      setTitleFieldError(t`An active period with this title already exists.`)
      return
    }
    onSubmit(activePeriod)

    setActivePeriod(initialActivePeriod)
    setEveryDayChecked(false)
    setWeekDaysChecked(false)
    setSaturdayChecked(false)
    setSundayChecked(false)
    setTitleFieldError(null)
    clearEdited()

    handleClose()
  }

  const getSubmitButtonTitle = () => {
    if (selectedActivePeriod) {
      return t`Save changes`
    }
    return selectedTab === PricingTabType.PRICINGS
      ? t`Add pricing exception`
      : t`Add exchange period exception`
  }

  return (
    <DrawerRight
      open={open}
      onClose={handleDrawerClose}
      sx={{ maxWidth: '550px', minWidth: '300px' }}
    >
      <StyledForm sx={{ p: 2 }} onSubmit={handleSubmit}>
        <Typography variant="h5" fontWeight="bold">
          {selectedTab === PricingTabType.PRICINGS
            ? t`Add pricing exception`
            : t`Add exchange period exception`}
        </Typography>
        <Box mt={1} ml={2}>
          <FormGroup>
            <TextField
              label={t`Title`}
              required
              variant="outlined"
              size="small"
              value={activePeriod.title ?? ''}
              onChange={e => {
                handleChange(e.target.value, 'title')
                setTitleFieldError(null)
              }}
              sx={{
                width: '320px',
                mb: 1,
                bgcolor: 'background.default'
              }}
              error={!!titleFieldError}
              helperText={titleFieldError ?? ''}
            />
            <Typography variant="body2" mb={1}>
              {selectedTab === PricingTabType.PRICINGS
                ? t`Choose the active period for the pricing exception.`
                : t`Choose the active period for the exchange period exception.`}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  onChange={handleEveryDayChange}
                  disabled={weekDaysChecked || saturdayChecked || sundayChecked}
                  checked={everyDayChecked}
                />
              }
              label={t`Everyday`}
            />
            <FormControlLabel
              control={
                <Switch
                  onChange={handleWeekDaysChange}
                  disabled={everyDayChecked}
                  checked={weekDaysChecked}
                />
              }
              label={t`Weekdays`}
            />
            <FormControlLabel
              control={
                <Switch
                  onChange={handleSaturdayChange}
                  disabled={everyDayChecked}
                  checked={saturdayChecked}
                />
              }
              label={t`Saturday`}
            />
            <FormControlLabel
              control={
                <Switch
                  onChange={handleSundayChange}
                  disabled={everyDayChecked}
                  checked={sundayChecked}
                />
              }
              label={t`Sunday`}
            />
            <Box ml={1}>
              <FormControlLabel
                control={
                  <Checkbox checked={wholeDayChecked} onChange={handleWholeDayCheckedChange} />
                }
                label={t`Whole day`}
              />
            </Box>
            <Box>
              {!wholeDayChecked && (
                <ActivePeriodTimePicker
                  activePeriod={activePeriod}
                  handleTimePickerChange={handleTimePickerChange}
                  isRequired={!wholeDayChecked}
                />
              )}
            </Box>
          </FormGroup>
          <Box display="flex" gap={2} mt={2}>
            <Button onClick={handleDrawerClose} size="medium" variant="outlined" color="inherit">
              {t`Cancel`}
            </Button>
            <CustomButton size="medium" type="submit" disabled={!hasUnsavedChanges}>
              {getSubmitButtonTitle()}
            </CustomButton>
          </Box>
        </Box>
      </StyledForm>
    </DrawerRight>
  )
}
